// ============================================================
// ByOurConsent.com -- FOIA & Public Records Generator
// /premium/foia
// Premium member tool -- Paperwork as a Weapon Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming','Washington D.C.'
];

const FEDERAL_AGENCIES = [
    'Federal Bureau of Investigation (FBI)',
    'Department of Justice (DOJ)',
    'Department of Homeland Security (DHS)',
    'Environmental Protection Agency (EPA)',
    'Internal Revenue Service (IRS)',
    'Department of Defense (DOD)',
    'Central Intelligence Agency (CIA)',
    'Department of State',
    'Department of Health and Human Services (HHS)',
    'Department of Education',
    'Department of Veterans Affairs (VA)',
    'Social Security Administration (SSA)',
    'Federal Communications Commission (FCC)',
    'Securities and Exchange Commission (SEC)',
    'Federal Trade Commission (FTC)',
    'Department of Treasury',
    'Department of Labor',
    'Department of Transportation',
    'Department of Energy',
    'Other federal agency'
];

const STATE_AGENCY_TYPES = [
    'Police department / Sheriff\'s office',
    'City council / Municipal government',
    'County government',
    'School board / School district',
    'State police / Highway patrol',
    'State attorney general\'s office',
    'State department of education',
    'State department of health',
    'State environmental agency',
    'State department of transportation',
    'Public utility / Water authority',
    'State legislature / Legislator\'s office',
    'Governor\'s office',
    'State court system',
    'Other state or local agency'
];

const RECORD_TYPES = [
    { id: 'emails',      label: 'Emails and communications',        detail: 'internal emails, memos, text messages between officials' },
    { id: 'contracts',   label: 'Contracts and procurement records', detail: 'vendor contracts, no-bid awards, purchase orders' },
    { id: 'budgets',     label: 'Budget and expenditure records',    detail: 'spending records, budget allocations, expense reports' },
    { id: 'minutes',     label: 'Meeting minutes and agendas',       detail: 'official meeting records, agenda items, votes' },
    { id: 'personnel',   label: 'Personnel records',                 detail: 'hiring records, disciplinary actions, salary information' },
    { id: 'incidents',   label: 'Incident and use of force reports', detail: 'police reports, use of force documentation, complaints' },
    { id: 'campaign',    label: 'Campaign finance records',          detail: 'donor lists, expenditure reports, PAC contributions' },
    { id: 'property',    label: 'Property and land records',         detail: 'permits, zoning decisions, property transactions' },
    { id: 'inspections', label: 'Inspection and enforcement records',detail: 'violations, citations, inspection reports' },
    { id: 'legal',       label: 'Legal actions and settlements',     detail: 'lawsuits, settlements, legal opinions' },
    { id: 'grants',      label: 'Grant and funding records',         detail: 'federal grants received, how funds were spent' },
    { id: 'other',       label: 'Other / Custom description',        detail: '' }
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Input styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

// ============================================================
// Main component
// ============================================================
const FoiaGeneratorPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    // Form state
    const [form, setForm] = useState({
        level:          'federal',       // federal | state
        state:          '',
        agency:         '',
        customAgency:   '',
        recordTypes:    [],
        customRecords:  '',
        dateFrom:       '',
        dateTo:         '',
        description:    ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const toggleRecord = (id) => {
        setForm(p => ({
            ...p,
            recordTypes: p.recordTypes.includes(id)
                ? p.recordTypes.filter(r => r !== id)
                : [...p.recordTypes, id]
        }));
    };

    // ============================================================
    // Generate FOIA letter via Anthropic API
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const selectedRecords = RECORD_TYPES
            .filter(r => form.recordTypes.includes(r.id))
            .map(r => r.label + (r.detail ? ` (${r.detail})` : ''))
            .join('\n- ');

        const agencyName = form.agency === 'Other federal agency' || form.agency === 'Other state or local agency'
            ? form.customAgency
            : form.agency;

        const dateRange = form.dateFrom || form.dateTo
            ? `Records from ${form.dateFrom || 'the beginning'} to ${form.dateTo || 'present'}.`
            : 'No specific date range specified — request all available records.';

        const prompt = `You are an expert in Freedom of Information Act (FOIA) law and public records requests. Generate a complete, legally formatted FOIA/public records request letter.

REQUEST DETAILS:
- Level: ${form.level === 'federal' ? 'Federal FOIA request' : `State public records request (${form.state})`}
- Agency: ${agencyName}
- Records requested:
- ${selectedRecords}
${form.customRecords ? `- Additional records: ${form.customRecords}` : ''}
- Date range: ${dateRange}
${form.description ? `- Additional context from requester: ${form.description}` : ''}

REQUIREMENTS:
1. If federal: cite the Freedom of Information Act, 5 U.S.C. § 552
2. If state: cite the correct state public records law for ${form.state} (e.g. Virginia FOIA, Texas Public Information Act, California Public Records Act, etc.) with correct statute citation
3. Include a fee waiver request citing public interest and the requester's intent to share information publicly
4. Include the correct legal deadline the agency must respond by (federal: 20 business days; cite state-specific deadline if state)
5. Include language about what to do if the request is denied or records are withheld — cite the right to appeal and administrative review
6. Use formal, professional legal language throughout
7. Format as a proper formal letter

FORMAT THE LETTER EXACTLY LIKE THIS:
[YOUR NAME]
[YOUR ADDRESS]
[YOUR CITY, STATE ZIP]
[YOUR EMAIL]
[YOUR PHONE]

[DATE]

FOIA/Public Records Officer
${agencyName}
[AGENCY ADDRESS]
[AGENCY CITY, STATE ZIP]

Re: Freedom of Information Act Request / Public Records Request

Dear FOIA Officer / Public Records Custodian:

[Body of letter with all required elements]

Sincerely,

[YOUR NAME]
[YOUR SIGNATURE]

---
ATTACHMENT: What to do if your request is denied
[Include a brief section explaining appeal rights and next steps if records are withheld or the request is denied]

Generate the complete letter now. Be thorough, legally precise, and include all required elements. Do not add any preamble or explanation outside the letter itself.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 2000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({ letter: text, agencyName, level: form.level, state: form.state });
            setStep(3);
        } catch (err) {
            setError('Failed to generate letter. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download as Word document
    // ============================================================
    const downloadWord = () => {
        try {
            downloadReportAsWord(
                result.letter,
                `FOIA-Request-${result.agencyName.replace(/[^a-z0-9]/gi, '-').slice(0, 40)}`
            );
        } catch (err) {
            // Fallback: plain text download
            const blob = new Blob([result.letter], { type: 'text/plain' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `FOIA-Request.txt`;
            a.click();
            URL.revokeObjectURL(url);
        }
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet>
                <title>FOIA Generator — By Our Consent</title>
            </Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Paperwork as a weapon
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        FOIA & Public Records Generator
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Generate a legally formatted public records request in three steps.
                        Includes correct state citations, fee waiver, deadlines, and appeal language.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Agency ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Who are you requesting records from?
                        </div>

                        {/* Federal or State */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Request type</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {[['federal', 'Federal agency (FOIA)'], ['state', 'State / Local agency']].map(([val, label]) => (
                                    <button key={val} onClick={() => set('level', val)}
                                        style={{ flex: 1, padding: '10px', background: form.level === val ? '#8b1a1a' : '#f5f0e8', color: form.level === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.level === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* State selector */}
                        {form.level === 'state' && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>State</label>
                                <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                    <option value="">Select state...</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Agency selector */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Agency</label>
                            <select style={iStyle} value={form.agency} onChange={e => set('agency', e.target.value)}>
                                <option value="">Select agency type...</option>
                                {(form.level === 'federal' ? FEDERAL_AGENCIES : STATE_AGENCY_TYPES).map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>

                        {/* Custom agency name */}
                        {(form.agency === 'Other federal agency' || form.agency === 'Other state or local agency' || form.agency) && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>
                                    {form.agency === 'Other federal agency' || form.agency === 'Other state or local agency'
                                        ? 'Agency name'
                                        : 'Specific agency / department name (optional)'}
                                </label>
                                <input style={iStyle} type="text"
                                    placeholder={form.agency === 'Other federal agency' || form.agency === 'Other state or local agency'
                                        ? 'Enter the full agency name...'
                                        : `e.g. "Norfolk Police Department" or "Richmond City Council"`}
                                    value={form.customAgency}
                                    onChange={e => set('customAgency', e.target.value)} />
                            </div>
                        )}

                        <button
                            onClick={() => setStep(2)}
                            disabled={!form.agency || (form.level === 'state' && !form.state)}
                            style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer', opacity: (!form.agency || (form.level === 'state' && !form.state)) ? 0.5 : 1 }}>
                            Next: What records? →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Records ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — What records are you requesting?
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Record types — check everything that might apply</div>
                                <div style={{ marginBottom: '10px' }}>
                                    Check all categories that could contain relevant records — even if you're not sure. 
                                    A broader request is harder to deny on narrow grounds. The AI will generate 
                                    appropriately scoped language for each type you select.
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Additional details — be specific about what you want</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Use this field to name specific documents, programs, people, companies, or 
                                    contracts you are looking for. The more specific, the harder it is for the 
                                    agency to claim they can't find anything.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Investigative notices, investigation files, 
                                    and communication logs related to Information Blocking enforcement under the 
                                    21st Century Cures Act involving MediCore Systems, a long-term care electronic 
                                    health records vendor."
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Investigation description — give context, not conclusions</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Describe what you are investigating in plain terms. You do not need to prove 
                                    wrongdoing — just explain what you are looking into. This is not included 
                                    verbatim in the letter but helps the AI draft more targeted request language.
                                    You are not required by law to explain why you want records.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Investigating potential Information Blocking 
                                    violations by MediCore Systems under the 21st Century Cures Act. Seeking records 
                                    of any OIG investigations, notices of investigation, civil money penalty 
                                    proceedings, or communications between HHS/OIG and MediCore Systems regarding 
                                    information blocking compliance."
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Date range — narrow it when you can</div>
                                <div style={{ marginBottom: '4px' }}>
                                    If you know when an event occurred or a law took effect, use that as your 
                                    start date. A narrower date range gets you more focused results and speeds 
                                    up the agency's response. Leave blank to request all available records.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> Start: April 2021 (when 21st Century Cures 
                                    Act information blocking rules took effect) · End: present
                                </div>
                            </div>
                        </details>

                        {/* Record type checkboxes */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Select all that apply</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {RECORD_TYPES.filter(r => r.id !== 'other').map(r => (
                                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.recordTypes.includes(r.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.recordTypes.includes(r.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{r.label}</div>
                                            {r.detail && <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{r.detail}</div>}
                                        </div>
                                        <input type="checkbox"
                                            checked={form.recordTypes.includes(r.id)}
                                            onChange={() => toggleRecord(r.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Custom records */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Other records / Additional details</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="Describe any additional records or add specific details about what you are looking for..."
                                value={form.customRecords}
                                onChange={e => set('customRecords', e.target.value)} />
                        </div>

                        {/* Date range */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Date range (optional)</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '3px' }}>From</div>
                                    <input style={iStyle} type="date" value={form.dateFrom} onChange={e => set('dateFrom', e.target.value)} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '3px' }}>To</div>
                                    <input style={iStyle} type="date" value={form.dateTo} onChange={e => set('dateTo', e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {/* What prompted this */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Brief description of what you are investigating (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="e.g. I am investigating a no-bid contract awarded to a company owned by a city council member's family member..."
                                value={form.description}
                                onChange={e => set('description', e.target.value)} />
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '3px' }}>
                                This helps generate more targeted request language. Not included verbatim in the letter.
                            </div>
                        </div>

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setStep(1)}
                                style={{ background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                ← Back
                            </button>
                            <button
                                onClick={generate}
                                disabled={form.recordTypes.length === 0 && !form.customRecords || loading}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 28px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer', opacity: (form.recordTypes.length === 0 && !form.customRecords) || loading ? 0.6 : 1 }}>
                                {loading ? 'Generating your request...' : 'Generate FOIA Request →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                ⏳ Researching applicable laws and drafting your request...
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Your FOIA request is ready
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>
                                    Review the letter below, then download and send.
                                </div>
                            </div>
                            <button onClick={downloadWord}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                ⬇ Download Word Doc
                            </button>
                        </div>

                        {/* Instructions */}
                        <div style={{ background: '#e8f5e0', border: '0.5px solid #a8d0a0', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11', lineHeight: 1.6 }}>
                            <strong>Before sending:</strong> Replace all [YOUR NAME], [YOUR ADDRESS], [DATE], and [AGENCY ADDRESS] placeholders with your actual information.
                            You do not need to explain why you want the records — that is your legal right.
                        </div>

                        {/* Letter preview */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', whiteSpace: 'pre-wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.letter}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer' }}>
                                ⬇ Download Word Doc
                            </button>
                            <button onClick={() => { setStep(1); setResult(null); setForm({ level: 'federal', state: '', agency: '', customAgency: '', recordTypes: [], customRecords: '', dateFrom: '', dateTo: '', description: '' }); }}
                                style={{ background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                Start a new request
                            </button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This letter is generated as a starting point for your public records request. Review it carefully before sending.
                            By Our Consent is not a law firm and this is not legal advice. For complex situations, consult a qualified attorney.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default FoiaGeneratorPage;
